import { z } from 'zod'
import { generatedAssetSchema } from './ai-studio-asset'
import { SOCIAL_IMAGE_CREDIT_COST } from './ai-studio-generate'
import { SOCIAL_FRAMES, type SocialFrame } from './crossposting'

export const formatPresetSchema = z.enum([
  'instagram-square',
  'instagram-portrait',
  'instagram-story',
  'facebook-post',
  'pulsify-cover',
  'email-header',
  'whatsapp-status',
  'facebook-wide',
  'flyer-a6',
])

export type FormatPreset = z.infer<typeof formatPresetSchema>

export const FORMAT_PRESET_DIMENSIONS: Record<FormatPreset, SocialFrame> = {
  // The social presets read the shared frames so a converted image lands exactly
  // where the composer would crop it to; the last two are ours, not Meta's.
  'instagram-square': SOCIAL_FRAMES.square,
  'instagram-portrait': SOCIAL_FRAMES.portrait,
  'instagram-story': SOCIAL_FRAMES.story,
  'facebook-post': SOCIAL_FRAMES.facebookPost,
  'pulsify-cover': { width: 1536, height: 1024 },
  'email-header': { width: 1200, height: 400 },
  'whatsapp-status': SOCIAL_FRAMES.story,
  'facebook-wide': { width: 1200, height: 628 },
  'flyer-a6': { width: 1240, height: 1748 },
}

/** Credit cost per requested preset, priced like an AI picture since each one is a new generation. */
export const FORMAT_CONVERSION_CREDIT_COST = SOCIAL_IMAGE_CREDIT_COST

export const formatOutputSchema = z.enum(['webp', 'png', 'jpeg'])

export type FormatOutput = z.infer<typeof formatOutputSchema>

/** Longest note on what the converted image should bring forward. */
export const FORMAT_FOCUS_MAX = 200

export const convertFormatRequestSchema = z.object({
  presets: z.array(formatPresetSchema).min(1).max(formatPresetSchema.options.length),
  output: formatOutputSchema.default('webp'),
  /** What should stand out, e.g. "the singer's face, closer up"; the rest gets less room. */
  focus: z.string().trim().max(FORMAT_FOCUS_MAX).optional(),
})

export type ConvertFormatRequest = z.infer<typeof convertFormatRequestSchema>

export const convertFormatResponseSchema = z.object({
  assets: z.array(generatedAssetSchema),
})

export type ConvertFormatResponse = z.infer<typeof convertFormatResponseSchema>
